import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FileText, Loader2, Paperclip, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { hubModalOverlayClassName, hubModalOverlayProps, hubNestedModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { hubInlinePreviewFrameClassName } from "@/lib/hubLayoutClassNames";
import { cn, formatTimestamp } from "@/lib/utils";

import {
  assignInsideSalesApproval,
  commentInsideSalesApproval,
  fetchApprovalDocumentPreview,
  fetchInsideSalesApproval,
  fetchInsideSalesApprovalSettings,
  INSIDE_SALES_APPROVALS_QUERY_KEY,
  linkInsideSalesApproval,
  patchInsideSalesApproval,
  searchInsideSalesApprovalDocuments,
  uploadInsideSalesApprovalDocument,
  type ApprovalKind,
} from "./insideSalesApprovalsApi";

const TYPE_OPTIONS: Array<{ value: ApprovalKind; label: string }> = [
  { value: "order", label: "Order" },
  { value: "quote", label: "Quote" },
  { value: "rma", label: "RMA" },
];

function normalizeExternalUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") return trimmed;
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function kindLabel(kind: ApprovalKind): string {
  if (kind === "rma") return "RMA";
  if (kind === "quote") return "Quote";
  return "Order";
}

export function InsideSalesApprovalTicketPanel({
  approvalId,
  onClose,
}: {
  approvalId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useHubModuleWrite("inside_sales");
  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [typeDraft, setTypeDraft] = useState<ApprovalKind>("order");
  const [sourceLinkDraft, setSourceLinkDraft] = useState("");
  const [documentSearch, setDocumentSearch] = useState("");
  const [debouncedDocumentSearch, setDebouncedDocumentSearch] = useState("");
  const [activityNoteDraft, setActivityNoteDraft] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [viewerDocumentId, setViewerDocumentId] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState("");

  const detailQuery = useQuery({
    queryKey: ["inside-sales", "approvals", approvalId],
    queryFn: () => fetchInsideSalesApproval(approvalId),
  });
  const settingsQuery = useQuery({
    queryKey: ["inside-sales", "approvals", "settings"],
    queryFn: fetchInsideSalesApprovalSettings,
  });

  useEffect(() => {
    const request = detailQuery.data?.request;
    if (!request) return;
    setNameDraft(request.name);
    setDescriptionDraft(request.description ?? "");
    setTypeDraft(request.kind);
    setSourceLinkDraft(request.sourceTicketUrl ?? "");
  }, [detailQuery.data?.request]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (viewerDocumentId || viewerUrl) {
          setViewerDocumentId(null);
          setViewerUrl(null);
          return;
        }
        onClose();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, viewerDocumentId, viewerUrl]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedDocumentSearch(documentSearch.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [documentSearch]);

  const documentOptionsQuery = useQuery({
    queryKey: ["inside-sales", "approvals", "document-options", typeDraft, debouncedDocumentSearch],
    queryFn: () => searchInsideSalesApprovalDocuments(typeDraft === "quote" ? "quote" : typeDraft === "rma" ? "rma" : "order", debouncedDocumentSearch),
    enabled: Boolean(detailQuery.data) && (typeDraft === "order" || typeDraft === "quote" || typeDraft === "rma"),
  });

  const previewQuery = useQuery({
    queryKey: ["inside-sales", "approvals", approvalId, "document", viewerDocumentId],
    queryFn: () => fetchApprovalDocumentPreview(approvalId, viewerDocumentId ?? ""),
    enabled: Boolean(approvalId && viewerDocumentId),
  });
  useEffect(() => {
    const url = previewQuery.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewQuery.data]);

  const persistDetail = useMutation({
    mutationFn: () =>
      patchInsideSalesApproval(approvalId, {
        name: nameDraft.trim(),
        description: descriptionDraft,
        type: typeDraft,
        sourceTicketUrl: sourceLinkDraft.trim() || null,
      }),
    onSuccess: async (detail) => {
      queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not save ticket.", tone: "error" }),
  });
  const assignMutation = useMutation({
    mutationFn: (assigneeUserId: string) => assignInsideSalesApproval(approvalId, assigneeUserId),
    onSuccess: async (detail) => {
      queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
      showToast({ message: "Assignee updated.", tone: "success" });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not assign request.", tone: "error" }),
  });
  const commentMutation = useMutation({
    mutationFn: (body: string) => commentInsideSalesApproval(approvalId, body),
    onSuccess: async (detail) => {
      setActivityNoteDraft("");
      queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
      showToast({ message: "Comment added.", tone: "success" });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not add comment.", tone: "error" }),
  });
  const attachmentMutation = useMutation({
    mutationFn: (file: File) => uploadInsideSalesApprovalDocument(approvalId, file),
    onSuccess: async (detail) => {
      queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
      showToast({ message: "File attached.", tone: "success" });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not attach file.", tone: "error" }),
  });
  const linkDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      linkInsideSalesApproval(
        approvalId,
        typeDraft === "quote" ? { quoteId: documentId } : { salesOrderId: documentId },
      ),
    onSuccess: async (detail) => {
      queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
      showToast({ message: "Document linked.", tone: "success" });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not link document.", tone: "error" }),
  });

  const userOptions = useMemo(() => {
    const needle = assigneeSearch.trim().toLowerCase();
    return (settingsQuery.data?.userOptions ?? [])
      .map((user) => ({ value: user.userId, label: user.displayName?.trim() || user.email }))
      .filter((option) => !needle || option.label.toLowerCase().includes(needle));
  }, [assigneeSearch, settingsQuery.data]);

  const documentSelectOptions = useMemo(
    () =>
      (documentOptionsQuery.data?.documents ?? []).map((document) => ({
        value: document.id,
        label: document.number,
        description: [document.clientName, document.endUserName, document.date].filter(Boolean).join(" · "),
        statusTone: document.statusTone,
        className: document.statusTone === "approved" ? "text-emerald-700" : "text-amber-700",
      })),
    [documentOptionsQuery.data],
  );

  const request = detailQuery.data?.request;
  const overlay = hubModalOverlayProps();
  const selectedDocument = (detailQuery.data?.documents ?? []).find((document) => document.id === viewerDocumentId);
  const isPdf =
    selectedDocument?.contentType === "application/pdf"
    || selectedDocument?.fileName.toLowerCase().endsWith(".pdf")
    || (viewerUrl != null && /\.pdf($|\?)/i.test(viewerUrl));

  return createPortal(
    <>
      <div
        {...overlay}
        className={cn(hubModalOverlayClassName(), "items-stretch justify-end p-0")}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <aside className="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl">
          {detailQuery.isLoading ? (
            <div className="flex flex-1 items-center justify-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading ticket...
            </div>
          ) : detailQuery.isError || !request ? (
            <div className="flex flex-1 flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <h2 className="text-lg font-semibold text-slate-900">Ticket</h2>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Close" onClick={onClose}>
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <div className="p-4 text-sm text-rose-700">
                {detailQuery.error instanceof Error ? detailQuery.error.message : "Could not load ticket."}
              </div>
            </div>
          ) : (
            <>
              <div className="flex shrink-0 items-start gap-3 border-b border-slate-200 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Label htmlFor="approval-ticket-name" className="sr-only">Ticket name</Label>
                  <Input
                    id="approval-ticket-name"
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    onBlur={() => {
                      if (hubWritesAllowed && nameDraft.trim() !== "" && nameDraft.trim() !== request.name) {
                        persistDetail.mutate();
                      }
                    }}
                    className="border-transparent px-1 text-lg font-semibold shadow-none focus-visible:border-slate-300"
                    disabled={!hubWritesAllowed}
                  />
                </div>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label="Close" onClick={onClose}>
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="approval-ticket-assignee">Assignee</Label>
                    <SearchableSelect
                      id="approval-ticket-assignee"
                      value={request.assigneeUserId ?? ""}
                      options={userOptions}
                      searchValue={assigneeSearch}
                      onSearchChange={setAssigneeSearch}
                      onValueChange={(option) => { if (option) assignMutation.mutate(option.value); }}
                      selectedLabelOverride={request.assigneeDisplayName ?? undefined}
                      placeholder="Select assignee"
                      searchPlaceholder="Search users"
                      className="mt-1"
                      disabled={!hubWritesAllowed}
                    />
                  </div>
                  <div>
                    <Label htmlFor="approval-ticket-type">Type</Label>
                    <select
                      id="approval-ticket-type"
                      value={typeDraft}
                      onChange={(event) => {
                        const next = event.target.value as ApprovalKind;
                        setTypeDraft(next);
                        if (hubWritesAllowed) {
                          patchInsideSalesApproval(approvalId, { type: next }).then(async (detail) => {
                            queryClient.setQueryData(["inside-sales", "approvals", approvalId], detail);
                            await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_APPROVALS_QUERY_KEY });
                          }).catch((error: unknown) => {
                            showToast({ message: error instanceof Error ? error.message : "Could not update type.", tone: "error" });
                          });
                        }
                      }}
                      className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
                      disabled={!hubWritesAllowed}
                    >
                      {TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {typeDraft === "order" || typeDraft === "quote" || typeDraft === "rma" ? (
                  <div>
                    <Label htmlFor="approval-ticket-document">Document</Label>
                    <SearchableSelect
                      id="approval-ticket-document"
                      value={request.linkedEntityId ?? ""}
                      options={documentSelectOptions}
                      searchValue={documentSearch}
                      onSearchChange={setDocumentSearch}
                      onValueChange={(option) => {
                        if (option) linkDocumentMutation.mutate(option.value);
                      }}
                      selectedLabelOverride={request.linkedEntityNumber ?? undefined}
                      placeholder={typeDraft === "quote" ? "Search quotes" : "Search sales orders"}
                      searchPlaceholder={typeDraft === "quote" ? "Quote number, client, or end user" : "Order number, client, or end user"}
                      emptyMessage={documentOptionsQuery.isLoading ? "Searching..." : "No matching documents."}
                      isLoading={documentOptionsQuery.isLoading || linkDocumentMutation.isPending}
                      filterLocally={false}
                      className="mt-1"
                      disabled={!hubWritesAllowed}
                      optionTrailing={(option) => {
                        const tone = documentSelectOptions.find((item) => item.value === option.value)?.statusTone;
                        return (
                          <span className={tone === "approved" ? "text-xs font-medium text-emerald-600" : "text-xs font-medium text-amber-600"}>
                            {tone === "approved" ? "Approved" : "Pending"}
                          </span>
                        );
                      }}
                    />
                  </div>
                ) : null}

                <div>
                  <Label htmlFor="approval-ticket-source">Source link</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="approval-ticket-source"
                      value={sourceLinkDraft}
                      onChange={(event) => setSourceLinkDraft(event.target.value)}
                      onBlur={() => {
                        if (hubWritesAllowed && sourceLinkDraft.trim() !== (request.sourceTicketUrl ?? "")) {
                          persistDetail.mutate();
                        }
                      }}
                      placeholder="https://"
                      disabled={!hubWritesAllowed}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={sourceLinkDraft.trim() === ""}
                      onClick={() => window.open(normalizeExternalUrl(sourceLinkDraft), "_blank", "noopener,noreferrer")}
                    >
                      <ExternalLink className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="approval-ticket-description">Description</Label>
                  <TextArea
                    id="approval-ticket-description"
                    value={descriptionDraft}
                    onChange={(event) => setDescriptionDraft(event.target.value)}
                    onBlur={() => {
                      if (hubWritesAllowed && descriptionDraft !== (request.description ?? "")) {
                        persistDetail.mutate();
                      }
                    }}
                    placeholder="Add a description..."
                    className="mt-1 min-h-[7rem] font-sans"
                    disabled={!hubWritesAllowed}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-slate-900">Attachments</div>
                    {hubWritesAllowed ? (
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="application/pdf,image/png,image/jpeg,image/webp,.pdf,.png,.jpg,.jpeg,.webp"
                          className="hidden"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (file) attachmentMutation.mutate(file);
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          isLoading={attachmentMutation.isPending}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Attach file
                        </Button>
                      </>
                    ) : null}
                  </div>
                  <div className="mt-2 space-y-2">
                    {(detailQuery.data?.documents ?? []).map((document) => (
                      <button
                        key={document.id}
                        type="button"
                        className="flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 hover:border-slate-300"
                        onClick={() => {
                          setViewerTitle(document.fileName);
                          setViewerUrl(null);
                          setViewerDocumentId(document.id);
                        }}
                      >
                        <Paperclip className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="min-w-0 truncate">{document.fileName}</span>
                      </button>
                    ))}
                    {(detailQuery.data?.documents.length ?? 0) === 0 ? (
                      <div className="rounded-md border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                        No attachments yet.
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-4">
                  <div className="text-sm font-semibold text-slate-900">Activity</div>
                  {hubWritesAllowed ? (
                    <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                      <Label htmlFor="approval-ticket-note">Add a comment</Label>
                      <TextArea
                        id="approval-ticket-note"
                        value={activityNoteDraft}
                        onChange={(event) => setActivityNoteDraft(event.target.value)}
                        placeholder="Leave a comment..."
                        className="mt-1 min-h-[4.5rem] font-sans"
                        disabled={commentMutation.isPending}
                      />
                      <div className="mt-2 flex justify-end">
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={activityNoteDraft.trim() === "" || commentMutation.isPending}
                          isLoading={commentMutation.isPending}
                          onClick={() => commentMutation.mutate(activityNoteDraft.trim())}
                        >
                          Add comment
                        </Button>
                      </div>
                    </div>
                  ) : null}
                  {(detailQuery.data?.activity.length ?? 0) === 0 ? (
                    <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                      No changes have been logged yet.
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      {(detailQuery.data?.activity ?? []).map((entry) =>
                        entry.kind === "comment" ? (
                          <div key={entry.id} className="rounded-md border border-slate-200 px-3 py-2">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-sm font-medium text-slate-900">Comment</div>
                              <div className="shrink-0 text-xs text-slate-500">
                                {formatTimestamp(entry.createdAt)}
                                {entry.actorDisplayName ? ` • ${entry.actorDisplayName}` : ""}
                              </div>
                            </div>
                            {entry.body.trim() !== "" ? (
                              <div className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">{entry.body}</div>
                            ) : null}
                          </div>
                        ) : (
                          <div key={entry.id} className="flex items-baseline justify-between gap-3 px-1 py-0.5 text-[11px] leading-4 text-slate-400">
                            <div className="min-w-0 truncate">
                              {entry.action || "Update"}
                              {entry.body.trim() !== "" ? ` · ${entry.body}` : ""}
                            </div>
                            <div className="shrink-0">
                              {formatTimestamp(entry.createdAt)}
                              {entry.actorDisplayName ? ` • ${entry.actorDisplayName}` : ""}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </aside>
      </div>

      {viewerDocumentId || viewerUrl ? (
        <div
          {...hubNestedModalOverlayProps()}
          className={cn(hubModalOverlayClassName(true), "items-center justify-center")}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setViewerDocumentId(null);
              setViewerUrl(null);
            }
          }}
        >
          <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
              <h2 className="min-w-0 truncate text-lg font-semibold text-slate-900">{viewerTitle || "Source document"}</h2>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Close viewer"
                onClick={() => {
                  setViewerDocumentId(null);
                  setViewerUrl(null);
                }}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>
            <div className="min-h-[24rem] flex-1 bg-slate-50 p-3">
              {viewerDocumentId && previewQuery.isLoading ? (
                <div className="flex h-full items-center justify-center gap-2 text-sm text-slate-600">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading preview...
                </div>
              ) : null}
              {viewerDocumentId && previewQuery.isError ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{previewQuery.error.message}</div>
              ) : null}
              {(previewQuery.data || viewerUrl) && isPdf ? (
                <iframe
                  title={viewerTitle || "Source document"}
                  src={previewQuery.data || viewerUrl || ""}
                  className={cn(hubInlinePreviewFrameClassName(), "h-[70vh] rounded-md border border-slate-200")}
                />
              ) : null}
              {(previewQuery.data || viewerUrl) && !isPdf ? (
                <img
                  src={previewQuery.data || viewerUrl || ""}
                  alt={viewerTitle || "Source document"}
                  className="max-h-[70vh] w-full rounded-md border border-slate-200 object-contain"
                />
              ) : null}
              {!viewerDocumentId && !viewerUrl ? (
                <div className="flex h-full flex-col items-center justify-center text-sm text-slate-500">
                  <FileText className="mb-2 h-10 w-10 text-slate-300" aria-hidden />
                  No preview
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>,
    document.body,
  );
}

export { kindLabel };
